import Swiper from "swiper";

const MD_BREAKPOINT = 744;
const LG_BREAKPOINT = 1280;
const GAP = 24;

export default function initProjectListCarousel() {
  const wrappers = document.querySelectorAll(".project-list-swiper-wrapper");

  if (!wrappers.length) return;

  wrappers.forEach((wrapper) => {
    const container = wrapper.querySelector(".project-list-swiper");
    const track = container?.querySelector(":scope > .swiper-wrapper");
    const slides = track?.querySelectorAll(":scope > .swiper-slide");
    const controls = wrapper.querySelector(":scope > .carousel-controls");
    const prevButton = controls?.querySelector(".carousel-button-prev");
    const nextButton = controls?.querySelector(".carousel-button-next");
    const progressBar = controls?.querySelector(".carousel-progress-bar");

    // Content fade slider (sibling of wrapper, both inside container)
    const parentContainer = wrapper.parentElement;
    const contentWrapper = parentContainer?.querySelector(
      ".project-list-content-wrapper"
    );
    const contentSlides = contentWrapper?.querySelectorAll(
      ".project-list-content-swiper > .swiper-wrapper > .swiper-slide"
    );

    if (!container || !track || !slides || slides.length === 0) return;

    const lastSlide = slides[slides.length - 1];
    const hasSeeAll = lastSlide?.classList.contains("see-all");
    const totalSlides = slides.length;
    const navigableSlides = hasSeeAll ? totalSlides - 1 : totalSlides;
    let currentIndex = 0;
    let swiper = null;

    // Hide controls if only 1 navigable slide
    if (controls && navigableSlides <= 1) {
      controls.style.display = "none";
    }

    // Initialize content slides - show first, hide others
    function initContentSlides() {
      if (!contentSlides) return;
      contentSlides.forEach((slide, i) => {
        slide.classList.toggle("is-active", i === 0);
      });
    }

    // Sync content slides to index with fade effect
    function syncContentSlides(index) {
      if (!contentSlides) return;
      contentSlides.forEach((slide, i) => {
        slide.classList.toggle("is-active", i === index);
      });
    }

    const mediaQuery = window.matchMedia(`(min-width: ${MD_BREAKPOINT}px)`);

    function handlePrev() {
      if (mediaQuery.matches) {
        if (currentIndex > 0) {
          currentIndex--;
          updateDesktop();
        }
      } else {
        if (swiper) swiper.slidePrev();
      }
    }

    function handleNext() {
      if (mediaQuery.matches) {
        if (currentIndex < navigableSlides - 1) {
          currentIndex++;
          updateDesktop();
        }
      } else {
        if (swiper) swiper.slideNext();
      }
    }

    if (prevButton) {
      prevButton.addEventListener("click", handlePrev);
    }
    if (nextButton) {
      nextButton.addEventListener("click", handleNext);
    }

    function initDesktop() {
      if (swiper) {
        swiper.destroy(true, true);
        swiper = null;
      }

      // Restore see-all slide visibility for desktop
      if (hasSeeAll) lastSlide.style.display = "";

      track.style.transform = "";
      slides.forEach((slide) => slide.classList.remove("is-active"));
      slides[0].classList.add("is-active");
      currentIndex = 0;

      updateDesktop();
    }

    function updateDesktop() {
      slides.forEach((slide, i) => {
        slide.classList.toggle("is-active", i === currentIndex);
      });

      const containerWidth = container.offsetWidth;
      // Use 75/25 ratio for lg, 65/35 ratio for md
      const isLg = window.innerWidth >= LG_BREAKPOINT;
      const smallRatio = isLg ? 0.25 : 0.35;
      const gapMultiplier = isLg ? 0.75 : 0.65;
      const smallWidth = containerWidth * smallRatio - GAP * gapMultiplier;
      const translateX = currentIndex * (smallWidth + GAP);
      track.style.transform = `translateX(-${translateX}px)`;

      // Disable at boundaries
      if (prevButton) {
        prevButton.classList.toggle(
          "swiper-button-disabled",
          currentIndex === 0
        );
      }
      if (nextButton) {
        nextButton.classList.toggle(
          "swiper-button-disabled",
          currentIndex >= navigableSlides - 1
        );
      }

      if (progressBar) {
        const progress = ((currentIndex + 1) / navigableSlides) * 100;
        progressBar.style.width = `${progress}%`;
      }

      // Sync content slides
      syncContentSlides(currentIndex);
    }

    function initMobile() {
      if (swiper) {
        swiper.destroy(true, true);
        swiper = null;
      }

      slides.forEach((slide) => slide.classList.remove("is-active"));
      track.style.transform = "";

      // Hide see-all slide on mobile
      if (hasSeeAll) lastSlide.style.display = "none";

      swiper = new Swiper(container, {
        slidesPerView: 1,
        spaceBetween: GAP,
        on: {
          init: updateMobileUI,
          slideChange: updateMobileUI,
        },
      });
    }

    function updateMobileUI(swiperInstance) {
      const s = swiperInstance || swiper;
      if (!s) return;

      const activeIndex = s.activeIndex;

      if (prevButton) {
        prevButton.classList.toggle(
          "swiper-button-disabled",
          activeIndex === 0
        );
      }
      if (nextButton) {
        nextButton.classList.toggle(
          "swiper-button-disabled",
          activeIndex >= navigableSlides - 1
        );
      }

      if (progressBar) {
        const progress = ((activeIndex + 1) / navigableSlides) * 100;
        progressBar.style.width = `${progress}%`;
      }

      // Sync content slides
      syncContentSlides(activeIndex);
    }

    // Click inactive slide to navigate to it (desktop only)
    slides.forEach((slide, i) => {
      slide.addEventListener("click", () => {
        if (!mediaQuery.matches) return;
        if (i === currentIndex) return;
        if (hasSeeAll && i >= navigableSlides) return;
        currentIndex = i;
        updateDesktop();
      });
    });

    // Initialize content slides once
    initContentSlides();

    if (mediaQuery.matches) {
      initDesktop();
    } else {
      initMobile();
    }

    // Enable transitions after initial render
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        container.classList.add("is-ready");
      });
    });

    mediaQuery.addEventListener("change", (e) => {
      if (e.matches) {
        initDesktop();
      } else {
        initMobile();
      }
    });

    // Listen for lg breakpoint change to recalculate widths
    const lgMediaQuery = window.matchMedia(`(min-width: ${LG_BREAKPOINT}px)`);
    lgMediaQuery.addEventListener("change", () => {
      if (mediaQuery.matches) {
        updateDesktop();
      }
    });
  });
}
